export type JourneyId = "third-party-mcp" | "secret-block";

export type JourneyStatus =
  | "not-started"
  | "in-progress"
  | "done"
  | "unreadable";

export function otherProjectGuideJourney(id: JourneyId): JourneyId {
  return id === "third-party-mcp" ? "secret-block" : "third-party-mcp";
}

/** Catalog entries we surface first in the automatic project-guide chooser. */
// Exact native identities from server/internal/mcpregistry/oktaseed/oktaseed.go.
// Display titles are not identity or provenance evidence.
export const AUTOMATIC_CATALOG_SERVER_SPECIFIERS = [
  "app.linear/mcp",
  "com.notion/mcp",
  "com.vercel/mcp",
  "ai.granola/mcp",
  "com.ramp/mcp",
  "com.github/mcp",
] as const;

export const JOURNEY_STATUS_LABELS: Record<JourneyStatus, string> = {
  "not-started": "Not started",
  "in-progress": "In progress",
  done: "Done",
  unreadable: "Progress unavailable",
};

export type JourneyMeta = {
  id: JourneyId;
  headline: string;
  title: string;
  /** The observable win, stated as the user would describe it afterwards. */
  win: string;
  completion: {
    heading: string;
    body: string;
    primaryAction: string;
  };
  steps: string[];
  stepBlurbs: string[];
};

export type JourneyFixture = {
  meta: string;
  accent: string;
  event: {
    label: string;
  };
};

const SECRET_BLOCK_STEPS = [
  "Create a secrets policy set to deny",
  "Setup the observability plugin",
  "Add it to your agent",
  "Send a prompt with a synthetic secret",
  "Watch the block land",
];

const PROJECT_GUIDE_MCP_STEPS = [
  "Pick and set up a server",
  "Connect your client",
  "Ask the agent to list the tools",
  "Watch the first governed call",
];

export const PROJECT_GUIDE_COMPLETE = {
  eyebrow: "Both journeys complete",
  heading: "Both journeys are on the record.",
  body: "Traffic is proxied and recorded, and prompts are inspected before transport. Project home now shows live calls, policies, and risk events instead of this card.",
  primaryAction: "Go to project home",
} as const;

export const PROJECT_GUIDE_JOURNEYS: JourneyMeta[] = [
  {
    id: "secret-block",
    headline: "Secure AI usage",
    title: "Block a leaked credential mid-prompt",
    win: "Create a deny policy for secrets, then watch it stop a synthetic credential before it reaches the model.",
    completion: {
      heading: "The prompt was denied.",
      body: "The prompt matched the secrets policy and was rejected before the model answered. The finding sits in Risk Events with the rule that fired, the matched span, the severity, and who sent it.",
      primaryAction: "Open Risk Events",
    },
    steps: SECRET_BLOCK_STEPS,
    stepBlurbs: [
      "Create a secrets policy that denies prompts containing API keys, tokens, or private keys for everyone in the organization.",
      "The observability plugin sends agent activity to this project so policies can inspect prompts before they reach the model. Download it here, then install it in your client.",
      "Run the command below to install the observability plugin, then restart your agent so prompts can be inspected by this project's policies.",
      "This key is synthetic and inert. It exists so the rule has something real-shaped to catch. The call should not survive the machine.",
      "The policy denies the request and a finding lands in Risk Events. It carries the rule that fired, the matched span, the severity, and who sent it.",
    ],
  },
  {
    id: "third-party-mcp",
    headline: "Govern AI data access",
    title: "Deploy an MCP gateway",
    win: "Put a governed endpoint in front of a third-party MCP server, then watch the first authorized tool call arrive in your logs.",
    completion: {
      heading: "The path is governed.",
      body: "Your client now reaches the selected server through an endpoint you own. Tool lists are filtered to what each caller may use, every call lands in tool logs, and the vendor's server never changed. Remove the server and the path closes.",
      primaryAction: "Open tool logs",
    },
    steps: PROJECT_GUIDE_MCP_STEPS,
    stepBlurbs: [
      "The catalog lists curated MCP servers. Installing one creates a governed endpoint in front of the vendor's server — the vendor's URL is already known, and nothing upstream changes.",
      "Run the command below to add the governed endpoint to your client. Complete OAuth when the client connects; the proxy keeps vendor credentials off the developer's machine.",
      "Run this in the client you just configured. Listing tools reads nothing and writes nothing — it puts the first real request on the governed path.",
      "The endpoint checks the call against the caller's tool access and records it before forwarding. This is the first entry.",
    ],
  },
];

export const PROJECT_GUIDE_FIXTURES: Record<JourneyId, JourneyFixture> = {
  "third-party-mcp": {
    meta: "govern a third-party MCP",
    accent: "#2879D8",
    event: {
      label: "The call you watched",
    },
  },
  "secret-block": {
    meta: "block a leaked credential",
    accent: "#B45A28",
    event: {
      label: "The event you watched",
    },
  },
};
